import { useMemo } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Chip,
  Button,
  Avatar,
  Tooltip,
} from "@mui/material";
import { SEMANTIC, WHATSAPP_GREEN } from "./theme/brand";
import { canalLabel, etapaLabel } from "./pipelineUtils";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EventNoteIcon from "@mui/icons-material/EventNote";
import StarIcon from "@mui/icons-material/Star";

export default function DashboardOportunidades({ store, onOpenWA, onOpenContacto }) {
  const acciones = useMemo(() => {
    const arr = [];

    // 1. Leads calientes sin contactar recientemente
    store.leadsConScore.filter(l => l.score >= 40).slice(0, 3).forEach(l => {
      const dias = l.ultimoContacto
        ? Math.floor((Date.now() - new Date(l.ultimoContacto).getTime()) / 86400000)
        : null;
      if (dias === null || dias >= 2) {
        arr.push({
          tipo: "hot",
          lead: l,
          texto: `Contactar a ${l.nombre}`,
          subtexto: `Prioridad ${l.score}/100 · ${l.cursoInteres || canalLabel(l.canal)} · ${dias === null ? "Nunca contactado" : `Último contacto hace ${dias} días`}`,
          prioridad: l.score,
        });
      }
    });

    // 2. Suscripciones por vencer → oportunidad de renovación
    store.ventasActivas.filter(v => v.endDate).forEach(v => {
      const dias = Math.ceil((new Date(v.endDate).getTime() - Date.now()) / 86400000);
      if (dias >= 0 && dias <= 7) {
        arr.push({
          tipo: "renovar",
          lead: store.leads.find(l => l.id === v.leadId),
          texto: `Renovar suscripción de ${v.nombreCliente}`,
          subtexto: `${v.productoNombre} · Vence en ${dias} día${dias !== 1 ? "s" : ""} · ${store.config.moneda} ${v.total.toLocaleString()}`,
          prioridad: 90 - dias,
        });
      }
    });

    // 3. Leads interesados con actividad reciente
    store.leads
      .filter(l => l.etapa === "interesado")
      .forEach(l => {
        if (!arr.some(a => a.lead?.id === l.id)) {
          arr.push({
            tipo: "cerrar",
            lead: l,
            texto: `Cerrar venta con ${l.nombre}`,
            subtexto: `Etapa: ${etapaLabel(store.config, "interesado")} · ${l.cursoInteres || canalLabel(l.canal)}`,
            prioridad: 35,
          });
        }
      });

    return arr.sort((a, b) => b.prioridad - a.prioridad).slice(0, 6);
  }, [store.leadsConScore, store.ventasActivas, store.leads, store.config]);

  const dineroOportunidad = store.resumenComercial?.pipelineTotal || 0;

  if (acciones.length === 0) return null;

  const iconMap = {
    hot: <TrendingUpIcon sx={{ fontSize: 16, color: SEMANTIC.warning }} />,
    renovar: <EventNoteIcon sx={{ fontSize: 16, color: SEMANTIC.pipeline }} />,
    cerrar: <StarIcon sx={{ fontSize: 16, color: SEMANTIC.revenue }} />,
  };

  const colorMap = { hot: SEMANTIC.warningTint, renovar: SEMANTIC.pipelineTint, cerrar: SEMANTIC.revenueTint };

  return (
    <Card variant="outlined" sx={{ mt: 3, borderTop: 3, borderTopColor: "primary.main" }}>
      <CardContent sx={{ px: { xs: 1.5, sm: 2 }, py: { xs: 1.5, sm: 2 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1}
          mb={2}
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <TrendingUpIcon color="primary" />
              <Typography variant="subtitle1">Acciones recomendadas</Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary">Leads calientes, renovaciones próximas y cierres pendientes</Typography>
          </Box>
          {dineroOportunidad > 0 && (
            <Tooltip title="Suma de estimaciones de negocios abiertos. El puntaje ordena el seguimiento; no mide probabilidad de compra.">
              <Chip
                label={`≈ ${store.config.moneda} ${dineroOportunidad.toLocaleString()} estimado en pipeline`}
                color="info"
                variant="outlined"
                size="small"
                sx={{ maxWidth: "100%" }}
              />
            </Tooltip>
          )}
        </Stack>

        <Stack spacing={1}>
          {acciones.map((a, i) => (
            <Box
              key={i}
              sx={{
                display: "flex", gap: { xs: 1, sm: 1.5 }, alignItems: "center",
                p: { xs: 1, sm: 1.5 },
                borderRadius: 1, bgcolor: colorMap[a.tipo] || SEMANTIC.neutralTint,
              }}
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: "transparent", display: { xs: "none", sm: "flex" } }}>
                {iconMap[a.tipo]}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: { xs: "normal", sm: "nowrap" }, overflow: "hidden", textOverflow: "ellipsis" }}>{a.texto}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: { xs: "normal", sm: "nowrap" }, overflow: "hidden", textOverflow: "ellipsis", display: "block" }}>{a.subtexto}</Typography>
              </Box>
              <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                {a.lead?.telefono && onOpenWA && (
                  <Button
                    size="small"
                    variant="outlined"
                    aria-label="Enviar WhatsApp"
                    sx={{ minWidth: 36, color: WHATSAPP_GREEN, borderColor: WHATSAPP_GREEN }}
                    onClick={() => onOpenWA(a.lead)}
                  >
                    <WhatsAppIcon fontSize="small" />
                  </Button>
                )}
              </Stack>
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}
